import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { creatorAPI } from '../../../api';
import ItemRow from './ItemRow';
import RecentlyDecided from './RecentlyDecided';

const SECTIONS = [
  { key: 'task', labelKey: 'cr_todo', fallback: 'To do', phKey: 'cr_add_todo_ph', phFallback: 'Add a task\u2026', symbol: '\u25cb' },
  { key: 'idea', labelKey: 'cr_ideas_parked', fallback: 'Ideas parked', phKey: 'cr_add_idea_ph', phFallback: 'Park an idea\u2026', symbol: '\u2192' },
];

// Project card sections: Decided · To do · Ideas parked (like "ÉCRU Launch")
export default function ProjectSections({ project, t, locale, onChanged }) {
  const navigate = useNavigate();
  const [children, setChildren] = useState(null);
  const [drafts, setDrafts] = useState({ decision: '', task: '', idea: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    creatorAPI.items({ project_id: project.id })
      .then(({ data }) => setChildren(data))
      .catch(() => setChildren([]));
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const add = async (type) => {
    const text = (drafts[type] || '').trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await creatorAPI.capture(text, { type, project_id: project.id });
      setDrafts((d) => ({ ...d, [type]: '' }));
      load();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const toggleDone = async (item) => {
    await creatorAPI.update(item.id, { status: item.status === 'done' ? 'open' : 'done' });
    load();
    onChanged?.();
  };

  if (children === null) return <div className="spinner" style={{ margin: '32px auto' }} />;

  const byType = (typeKey) => {
    const list = children.filter((c) => c.type === typeKey);
    if (typeKey === 'task') {
      return [...list.filter((c) => c.status !== 'done'), ...list.filter((c) => c.status === 'done')];
    }
    return list;
  };
  const other = children.filter((c) => !['decision', 'task', 'idea'].includes(c.type || ''));

  return (
    <div className="crs-proj" data-testid="creator-project-sections">
      {/* Recently Decided — current key decisions only */}
      <section className="crs-proj__section is-decision" data-testid="creator-project-decision-section">
        <p className="crs-section__label">
          <span className="crs-proj__symbol">{'\u2713'}</span>
          {t.cr_recent_decisions || 'Recently decided'}
          {children.filter((c) => c.type === 'decision' && c.status !== 'done').length > 0 && (
            <span className="crs-section__count">
              {children.filter((c) => c.type === 'decision' && c.status !== 'done').length}
            </span>
          )}
        </p>
        <RecentlyDecided decisions={children.filter((c) => c.type === 'decision')} t={t} locale={locale} />
        <div className="crs-proj__add">
          <input
            value={drafts.decision}
            onChange={(e) => setDrafts((d) => ({ ...d, decision: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') add('decision'); }}
            placeholder={t.cr_add_decided_ph || 'Add a decision\u2026'}
            data-testid="creator-project-add-decision-input"
          />
          <button
            type="button"
            onClick={() => add('decision')}
            disabled={busy || !(drafts.decision || '').trim()}
            data-testid="creator-project-add-decision-btn"
          >+</button>
        </div>
      </section>

      {SECTIONS.map(({ key, labelKey, fallback, phKey, phFallback, symbol }) => {
        const list = byType(key);
        return (
          <section key={key} className={`crs-proj__section is-${key}`} data-testid={`creator-project-${key}-section`}>
            <p className="crs-section__label">
              <span className="crs-proj__symbol">{symbol}</span>
              {t[labelKey] || fallback}
              {list.length > 0 && <span className="crs-section__count">{list.length}</span>}
            </p>
            {list.length === 0 ? (
              <p className="crs-empty">{t.cr_area_empty || 'Nothing here yet.'}</p>
            ) : (
              <div className="crs-list">
                {list.map((it) => (
                  <ItemRow
                    key={it.id}
                    item={it}
                    t={t}
                    locale={locale}
                    onOpen={(x) => navigate(`/creator/item/${x.id}`)}
                    onToggleDone={toggleDone}
                  />
                ))}
              </div>
            )}
            <div className="crs-proj__add">
              <input
                value={drafts[key]}
                onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') add(key); }}
                placeholder={t[phKey] || phFallback}
                data-testid={`creator-project-add-${key}-input`}
              />
              <button
                type="button"
                onClick={() => add(key)}
                disabled={busy || !(drafts[key] || '').trim()}
                data-testid={`creator-project-add-${key}-btn`}
              >+</button>
            </div>
          </section>
        );
      })}

      {other.length > 0 && (
        <section className="crs-proj__section" data-testid="creator-project-related-section">
          <p className="crs-section__label">{t.cr_related || 'Related entries'}</p>
          <div className="crs-list">
            {other.map((it) => (
              <ItemRow
                key={it.id}
                item={it}
                t={t}
                locale={locale}
                onOpen={(x) => navigate(`/creator/item/${x.id}`)}
                onToggleDone={toggleDone}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
